import React from "react";
import PropTypes from "prop-types";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import useApi from "../hooks/useApi";

/**
 * AcceptedDomainDeleteModal component.
 *
 * Displays a modal dialog for deleting an accepted domain.
 *
 * @component
 *
 * @param {Object} props - Component props.
 * @param {Object} props.config - The configuration object containing URI information.
 * @param {boolean} props.isOpen - Flag to control whether the modal is open.
 * @param {Function} props.onChangeBulk - Callback function to handle bulk state changes.
 * @param {string} props.applicationId - The application identifier.
 * @param {string} props.environmentName - The name of the environment.
 * @param {string} props.acceptedDomain - The accepted domain to be deleted.
 *
 * @returns {JSX.Element} The AcceptedDomainDeleteModal component.
 */
const AcceptedDomainDeleteModal = (props) => {
	const {
		config,
		isOpen,
		onChangeBulk,
		applicationId,
		environmentName,
		acceptedDomain
	} = props;
	const api = useApi();

	/**
	 * Handles the deletion of an accepted domain.
	 *
	 * Performs an asynchronous DELETE request to remove the accepted domain using the API hook,
	 * then updates the parent component through onChangeBulk.
	 *
	 * @async
	 * @returns {Promise<void>} Returns a promise that resolves when the delete request is complete.
	 */
	const handleDelete = async () => {
		try {
			const application = await api.acceptedDomain.delete({
				applicationId: applicationId,
				environmentName: environmentName,
				acceptedDomain: acceptedDomain
			});
			onChangeBulk({
				acceptedDomainDeleteModalIsOpen: false,
				application: application,
				environments: application.environments
			});
		} catch (error) {
			console.error("Failed to delete accepted domain:", error);
		}
	};

	return (
		<Dialog
			open={isOpen}
			onClose={() => onChangeBulk({ acceptedDomainDeleteModalIsOpen: false })}
		>
			<DialogTitle>
				Delete "{acceptedDomain}" as an accepted domain?
			</DialogTitle>
			<DialogContent>
				<DialogContentText>
					WARNING: Deleting an accepted domain will prevent the frontend from being matched to an application environment API request. Are you sure?
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={() => onChangeBulk({ acceptedDomainDeleteModalIsOpen: false })}
				>
					Cancel
				</Button>
				<Button onClick={handleDelete} color="error">
					DELETE
				</Button>
			</DialogActions>
		</Dialog>
	);
};

AcceptedDomainDeleteModal.propTypes = {
	config: PropTypes.shape({
		uri: PropTypes.shape({
			base: PropTypes.string.isRequired
		}).isRequired
	}).isRequired,
	isOpen: PropTypes.bool.isRequired,
	onChangeBulk: PropTypes.func.isRequired,
	applicationId: PropTypes.string.isRequired,
	environmentName: PropTypes.string.isRequired,
	acceptedDomain: PropTypes.string.isRequired
};

export default AcceptedDomainDeleteModal;
